import { isBoolean, isNull, isNumber, isString, isUndefined } from "lodash";
import { ReferenceDateDimension } from "./types";
import { DatapointTimeseriesEnum, DatapointValueTypeEnum, DatapointValueVariationTypeEnum } from "api/ux/src";
import { getDateWhenString } from "common/date-utils";
import { nullableNumber } from "common/number-helper";
import { DataGranularity, DisplayValueType } from "store/current-dashboard/types";
import { getIdDimensionDatapointName } from "store/widget-workshop/utils";
const toApiResponseVariation = (variationType, value) => {
    const result = {
        change: value?.change?.value,
        changePercent: value?.percentChange
    };
    switch (variationType) {
        case DatapointValueVariationTypeEnum.Bool: return {
            ...result,
            currentValue: Number(value?.current?.value),
            previousValue: Number(value?.previous?.value)
        };
        case DatapointValueVariationTypeEnum.Number: return {
            ...result,
            currentValue: nullableNumber(value?.current?.value),
            previousValue: nullableNumber(value?.previous?.value)
        };
        case DatapointValueVariationTypeEnum.String: return {
            ...result,
            currentValue: value?.current?.value?.toString(),
            previousValue: value?.previous?.value?.toString()
        };
        case DatapointValueVariationTypeEnum.Money: return {
            ...result,
            currentValue: value?.current?.value,
            previousValue: value?.previous?.value
        };
        default: throw new Error(`${variationType} datapoint variation measure type not supported. object value was ${JSON.stringify(value)}`);
    }
};
// Return null if the dimension is not added
export const getAddedDimension = (componentProps) => {
    const dimensionName = componentProps.datapointParams?.[0]?.dimensions?.[0]?.name ?? "";
    return componentProps?.columnsTemplate?.columns
        ?.find((x) => getIdDimensionDatapointName(x.field) === dimensionName || x.field === dimensionName)?.field ?? null;
};
export const getMeasureValue = (d) => {
    switch (d.type) {
        case DatapointValueTypeEnum.Bool: return Boolean(d.value);
        case DatapointValueTypeEnum.Date: return d?.value;
        case DatapointValueTypeEnum.Money: return d?.value;
        case DatapointValueTypeEnum.Area: return d?.value;
        case DatapointValueTypeEnum.Unit: return d?.value;
        case DatapointValueTypeEnum.Number: return nullableNumber(d?.value);
        case DatapointValueTypeEnum.String: return d.value?.toString();
        case DatapointValueTypeEnum.Variation: return toApiResponseVariation(d.variationType, d?.value);
        case DatapointValueTypeEnum.Metadata: return d.value?.documentId?.toString();
        default: throw new Error(`${d.type} datapoint measure type not supported. object was ${JSON.stringify(d)}`);
    }
};
export const getVariationGetter = (displayType) => {
    let getter = (v) => v.currentValue;
    if (displayType === DisplayValueType.Percent) {
        getter = (v) => v.changePercent;
    }
    else if (displayType === DisplayValueType.Numeral) {
        getter = (v) => v.change;
    }
    return getter;
};
export const hasTotalPercentage = (measures) => "TotalPercentage" in (measures ?? {});
export const isVariation = (value) => {
    if (isBoolean(value) || isString(value) || isNull(value) || isUndefined(value) || isNumber(value)) {
        return false;
    }
    return "currentValue" in value;
};
export const isAmount = (value) => {
    if (isBoolean(value) || isString(value) || isNull(value) || isUndefined(value) || isNumber(value)) {
        return false;
    }
    return "amount" in value;
};
export const isArea = (value) => {
    if (isBoolean(value) || isString(value) || isNull(value) || isUndefined(value) || isNumber(value)) {
        return false;
    }
    return "area" in value;
};
export const isUnit = (value) => {
    if (isBoolean(value) || isString(value) || isNull(value) || isUndefined(value) || isNumber(value)) {
        return false;
    }
    return "value" in value;
};
export const getLeafValue = (value, variationGetter = (v) => v?.currentValue) => {
    let leafValue = value;
    if (isVariation(leafValue)) {
        leafValue = variationGetter(leafValue);
    }
    if (isAmount(leafValue)) {
        leafValue = leafValue.amount;
    }
    if (isArea(leafValue)) {
        leafValue = leafValue.area;
    }
    if (isUnit(leafValue)) {
        leafValue = leafValue.value;
    }
    return leafValue;
};
export const getMeasureValues = (d) => d?.map((c) => ({
    ...c,
    name: c.name || "",
    value: getMeasureValue(c)
})) || [];
export const getDimensionValue = (series, name) => {
    if (name === ReferenceDateDimension) {
        return {
            name,
            type: DatapointValueTypeEnum.Number,
            value: getDateWhenString(series.date)?.getTime()
        };
    }
    const datapoint = series.datapoints?.find((d) => d?.name === name);
    const type = datapoint?.type || DatapointValueTypeEnum.String;
    let value = datapoint?.value?.toString() || "";
    if (type === DatapointValueTypeEnum.Number && isFinite((datapoint?.value || NaN))) {
        value = Number(datapoint?.value);
    }
    return { name, type, value };
};
export const getHigherDimensionValues = (series, higherDimensionsFilters) => {
    const higherDimensionValues = [];
    const higherDimensions = Object.keys(higherDimensionsFilters);
    higherDimensions?.forEach((higherDimensionKey) => {
        const higherDimensionValue = getDimensionValue(series, higherDimensionKey);
        if (higherDimensionValue && higherDimensionValue?.value?.toString() === higherDimensionsFilters[higherDimensionKey]?.toString()) {
            higherDimensionValues.push(higherDimensionValue);
        }
    });
    return higherDimensionValues;
};
export const getDimensionUniqueValues = (series, dimensionName, higherDimensionsFilters) => {
    if ((series?.length ?? 0) < 1) {
        return { seriesWithDimensionLookup: {}, uniqueDimensions: [] };
    }
    const dimensionValues = [];
    const dimfilterlength = Object.keys(higherDimensionsFilters).length;
    const seriesWithDimensionLookup = {};
    series.forEach((s) => {
        if (s.date) {
            const higherDimensionValues = getHigherDimensionValues(s, higherDimensionsFilters);
            if (higherDimensionValues?.length === dimfilterlength) {
                const dimension = getDimensionValue(s, dimensionName);
                if (dimension?.value) {
                    const dimensionValue = dimension.value;
                    dimensionValues.push(dimensionValue);
                    if (!seriesWithDimensionLookup[dimensionValue]) {
                        seriesWithDimensionLookup[dimensionValue] = [];
                    }
                    seriesWithDimensionLookup[dimensionValue].push(s);
                }
            }
        }
    });
    return { seriesWithDimensionLookup, uniqueDimensions: [...new Set(dimensionValues)] };
};
export const getDataGranularity = (datapointTimeSeriesEnum) => {
    if (!datapointTimeSeriesEnum) {
        return datapointTimeSeriesEnum;
    }
    let result = null;
    if (datapointTimeSeriesEnum === DatapointTimeseriesEnum.EndOfYear) {
        result = DataGranularity.yearly;
    }
    else if (datapointTimeSeriesEnum === DatapointTimeseriesEnum.YearOverYear) {
        result = DataGranularity.yearOverYear;
    }
    else {
        result = DataGranularity.quarterly;
    }
    return result;
};
